import { useState } from "react";
import { RequestParameterSectionDto } from "../Schemas/RequestParameterSectionDto";
import { Box, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { DocumentsIcon, getCenteredSpaceBetweenStyles, getYCenterStyles, AngleSmallDownIcon, InfoIcon } from "vig-ui-library";
import { SectionOrFieldGroupDto } from "../Schemas/SectionOrFieldGroupDto";
import { ComponentSwitcher } from "./ComponentSwitcher";
import { PKPIconSwitcher } from "./PKPIconSwitcher";
import { utilities } from "../Utilities/file";
import { RequestFormContext } from "../RequestFormContext";
import { RequestSectionContainerLayoutEnum } from "../Enums/RequestSectionContainerLayout";
import { RequestParameterTypeEnum } from "../Enums/RequestParameterTypeEnum";
import { SectionDisplayTypeEnum } from "../Enums/SectionDisplayTypeEnum";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { isSectionOrFieldGroupHidden, sectionHasVisibleDescendants } from "../Utilities/mappers";
import { getIdentificatorHeadingForSection } from "../Utilities/identificatorHeading";

export type SectionProps = RequestFormContext & {
    section: RequestParameterSectionDto;
}

/** Section that shows a title. */
const isVisibleNestedSection = (s: RequestParameterSectionDto) =>
    Boolean(s.displayTitle?.trim());

/** True when any ancestor is a visible nested section — child sections render as flat headlines without card padding. */
const hasVisibleNestedAncestor = (
    section: RequestParameterSectionDto,
    allSections: RequestParameterSectionDto[] | undefined
): boolean => {
    if (!allSections?.length) return false;
    const byId = new Map(allSections.map((s) => [s.id, s]));
    let parentId = section.parentSectionId;
    while (parentId != null && String(parentId).trim() !== "") {
        const parent = byId.get(String(parentId));
        if (!parent) return false;
        if (isVisibleNestedSection(parent)) return true;
        parentId = parent.parentSectionId;
    }
    return false;
};

export const Section = ({ section, ...context }: SectionProps) => {
    const theme = useTheme();

    const getInitialCollapsedValue = () => {
        if (!section.collapsible || !section.collapsed) return false;
        return true;
    }

    const [collapsed, setCollapsed] = useState<boolean>(getInitialCollapsedValue());

    const toggleCollapsed = () => setCollapsed((prev) => !prev);

    /** Card/header padding styles: sections that have a visible title. */
    const isNestedSectionWithTitle =
        Boolean(section.displayTitle?.trim());

    const isNestedUnderVisibleSection = hasVisibleNestedAncestor(section, context.request.current.sections);

    const getAllFieldsInSection = (s: RequestParameterSectionDto): RequestFieldDto[] =>
        s.children.flatMap(group =>
            group.children.flatMap(child => {
                if (child.field) return [child.field];
                if (child.section) return getAllFieldsInSection(child.section);
                return [];
            }));

    const identificatorFieldTypes = [
        RequestParameterTypeEnum.PARTNER_ID,
        RequestParameterTypeEnum.SKODA_ID,
        RequestParameterTypeEnum.POISTKA_ID
    ];

    const allFieldsInSection = getAllFieldsInSection(section);

    const sectionContainIdentificatorField = allFieldsInSection.some(field =>
        identificatorFieldTypes.includes(field.type)
    );

    const sectionContainIdentificatorFieldWithOtherField =
        isNestedSectionWithTitle &&
        sectionContainIdentificatorField &&
        allFieldsInSection.some(field => !identificatorFieldTypes.includes(field.type));

    const firstIdentificatorType =
        allFieldsInSection.find((field) => identificatorFieldTypes.includes(field.type))?.type ?? null;
    const identificatorSectionHeading = getIdentificatorHeadingForSection(firstIdentificatorType);

    const headerHasContent = section.collapsible || section.displayTitle || section.infoText;

    const useTwoColumnLayout = section.layout === RequestSectionContainerLayoutEnum.ROW;

    if (
        !sectionHasVisibleDescendants(
            section,
            context.request.current,
            context.settings.current.odvetvieId,
            context.getRequestFieldsRelationTask.value?.fields ?? []
        )
    ) {
        return null;
    }

    const getComponentSwitchers = (): JSX.Element[] => {
        return section.children.map((child: SectionOrFieldGroupDto, index: number) => {
            const groupHidden = isSectionOrFieldGroupHidden(
                child,
                context.request.current,
                context.settings.current.odvetvieId,
                context.getRequestFieldsRelationTask.value?.fields ?? []
            );

            return (
                <Box
                    key={child.group || index}
                    className="section-item"
                    sx={{
                        ...(useTwoColumnLayout
                            ? {
                                // Grow to fill the row when a sibling is hidden (display:none
                                // removes it from flex). Two visible items still share ~50% each.
                                flex: {
                                    xs: "1 1 100%",
                                    md: `1 1 calc(50% - ${theme.spacing(0.75)})`,
                                },
                                maxWidth: "100%",
                                alignSelf: "flex-start",
                            }
                            : {
                                width: "100%",
                                display: "block",
                                "& > * + *": {
                                    marginTop: theme.spacing(1.5),
                                },
                            }),
                        ...(groupHidden ? { display: "none" } : {}),
                    }}
                >
                    <ComponentSwitcher
                        sectionOrFieldGroup={child}
                        {...context}
                        sectionContainIdentificatorFieldWithOtherField={sectionContainIdentificatorFieldWithOtherField}
                        pathToProp={`${context.pathToProp}.${utilities.createPathToProp("children", index)}`}
                    />
                </Box>
            );
        });
    }

    const isRootSection = section.parentSectionId == null && section.order <= 1;

    const useCardChrome = isNestedSectionWithTitle && !isNestedUnderVisibleSection;

    // Section DisplayType styling (null/1 => current look, 2 => gray background, 3 => gray border).
    const isGrayBox = section.displayType === SectionDisplayTypeEnum.GRAY_BOX;
    const isOutlinedBox = section.displayType === SectionDisplayTypeEnum.OUTLINED_BOX;
    const useBoxChrome = useCardChrome || isGrayBox || isOutlinedBox;

    return (
        <Box
            className={useCardChrome ? "section section--last" : "section"}
            data-section-contain-identificator-field={sectionContainIdentificatorField}
            data-section-contain-identificator-field-with-other-field={sectionContainIdentificatorFieldWithOtherField}
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                ...(useCardChrome ? {
                    backgroundColor: theme.palette.background.default,
                    borderRadius: "7px"
                } : {}),
                ...(isGrayBox ? {
                    backgroundColor: theme.palette.grey[50],
                    borderRadius: "5px"
                } : {}),
                ...(isOutlinedBox ? {
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "7px"
                } : {}),
            }}
        >
            {/* ---------------------------------------------------------------------------- */}
            {/* Section Header */}
            {/* ---------------------------------------------------------------------------- */}
            {sectionContainIdentificatorFieldWithOtherField && (
                <Box
                    className="section-header"
                    sx={{
                        ...getCenteredSpaceBetweenStyles(),
                        marginBottom: 1,
                        padding: "24px",
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        margin: 0
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: theme.spacing(2) }}>
                        <DocumentsIcon width={32} height={32} color={theme.palette.primary.main} />
                        {identificatorSectionHeading && (
                            <Typography variant="h6" component="p">
                                {identificatorSectionHeading}
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}
            {
                !sectionContainIdentificatorFieldWithOtherField && headerHasContent && (
                    <Box
                        className="section-header"
                        sx={{
                            ...getCenteredSpaceBetweenStyles(),
                            ...(isNestedUnderVisibleSection
                                ? { pt: theme.spacing(1.5), pb: 0, px: 0 }
                                : { padding: "24px" }),
                            borderBottom:
                                isNestedUnderVisibleSection || isRootSection
                                    ? null
                                    : `1px solid ${theme.palette.divider}`,
                            margin: 0,
                            marginBottom: isNestedUnderVisibleSection ? theme.spacing(0.5) : 0
                        }}
                    >
                        <Box sx={{ ...getYCenterStyles(), gap: 1 }}>
                            {section.collapsible && (
                                <IconButton onClick={toggleCollapsed}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
                                            transition: "transform 0.2s ease",
                                        }}
                                    >
                                        <AngleSmallDownIcon
                                            width={20}
                                            height={20}
                                            color={theme.palette.text.primary}
                                        />
                                    </Box>
                                </IconButton>
                            )}

                            {section.reactIcon && (
                                <PKPIconSwitcher
                                    iconKey={section.reactIcon}
                                    width={32}
                                    height={32}
                                    color={theme.palette.primary.main}
                                />
                            )}

                            <Typography
                                variant="h6"
                                component="p"
                                sx={(isGrayBox || isOutlinedBox) ? {
                                    color: theme.palette.grey[600],
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    lineHeight: "120%",
                                    letterSpacing: "0.39px",
                                    textTransform: "uppercase",
                                    fontFeatureSettings: "'liga' off, 'clig' off",
                                } : undefined}
                            >
                                {section.displayTitle}
                            </Typography>

                            {section.infoText && (
                                <Tooltip title={section.infoText}>
                                    <IconButton>
                                        <InfoIcon width={16} height={16} color={theme.palette.info.main} />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                )
            }

            {/* ---------------------------------------------------------------------------- */}
            {/* Section Body */}
            {/* ---------------------------------------------------------------------------- */}
            <Box
                className="component-switcher-holder"
                sx={{
                    flexDirection: useTwoColumnLayout ? "row" : "column",
                    display: collapsed ? "none" : "flex",
                    width: "100%",
                    flexWrap: useTwoColumnLayout ? "wrap" : "nowrap",
                    gap: 1.5,
                    ...(useTwoColumnLayout ? { alignItems: "flex-start" } : {}),
                    ...((isGrayBox || isOutlinedBox)
                        ? { padding: "20px" }
                        : useBoxChrome
                            ? { padding: "24px" }
                            : {})
                }}
            >
                {getComponentSwitchers()}
            </Box>
        </Box >
    );
}